import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Lock, Users } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const PasswordGate = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Auth is derived from app_password existing in sessionStorage. We keep
      // the password itself (not just a boolean flag) so the Listings +
      // Listing-detail admin features can POST it to the backend without a
      // second unlock step.
      const stored = sessionStorage.getItem('app_password');
      if (stored) {
        setAuthenticated(true);
        setLoading(false);
        return;
      }
      const jwt = localStorage.getItem('app_token');
      if (!jwt) {
        setLoading(false);
        return;
      }
      // Signed-in dashboard user (Phase 2 JWT). If they're an admin, fetch
      // the master password server-side so the legacy admin endpoints under
      // `/api/public/admin/*` keep working in this tab. sessionStorage is
      // per-tab so this lookup has to run every time `/listings` opens in a
      // new tab (e.g. via the "⇱ Listings" top-bar button).
      try {
        const res = await fetch(`${API}/api/auth/admin-password`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (res.ok) {
          const { password } = await res.json();
          if (password) sessionStorage.setItem('app_password', password);
        }
      } catch { /* non-admin / network — fall through, show master gate */ }
      if (!cancelled) {
        setAuthenticated(true);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API}/api/auth/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await res.json();
      if (data.valid) {
        sessionStorage.setItem('app_password', password);
        sessionStorage.setItem('app_authenticated', 'true');
        setAuthenticated(true);
      } else {
        setError('Invalid password');
        setPassword('');
      }
    } catch {
      setError('Failed to verify. Please try again.');
    }
  };

  if (loading) return null;
  if (authenticated) return children;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="password-gate">
      <Card className="w-full max-w-sm shadow-lg">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-5 w-5 text-primary" />
          </div>
          <CardTitle className="font-heading text-xl tracking-tight">Flex Living Miami</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Enter password to access dashboard</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <PasswordInput
              placeholder="Password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              autoFocus
              data-testid="password-gate-input"
            />
            {error && <p className="text-sm text-destructive text-center" data-testid="password-gate-error">{error}</p>}
            <Button type="submit" className="w-full" data-testid="password-gate-submit">
              Unlock
            </Button>
          </form>
          <div className="mt-5 pt-4 border-t border-border/60 text-center">
            <a
              href="/staff/login"
              className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
              data-testid="password-gate-staff-link"
            >
              <Users className="h-3.5 w-3.5" />
              Staff member? Sign in to staff portal
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
